/**
 * Import half of Settings → Backup & Transfer.
 *
 * Two steps on purpose. Picking a file only READS and validates it; nothing
 * is applied until the user sees what the file actually contains and presses
 * Import. That is what makes "the file was hand-edited" survivable: a hard
 * error stops at step one, having changed nothing.
 */
import { useState } from 'react';
import { FileUp, Upload } from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { toast } from '../../../components/ui/toaster';
import { useAnalytics } from '../../../core/analytics/useAnalytics';
import { applySettingsBundle } from '../model/applySettingsBundle';
import { parseSettingsBundle } from '../model/parseSettingsBundle';
import { SETTINGS_BUNDLE_GROUPS, type SettingsBundleGroupId } from '../model/settingsBundleManifest';
import type { BundleWarning, SettingsBundle } from '../model/settingsBundleTypes';
import { storeTarget } from '../services/settingsStoreTargets';
import {
  importBundleFromFile,
  importMetaInfoRecord,
  METAINFO_CAVEAT,
} from '../services/settingsTransferApi';
import { SettingsGroupChecklist, type ChecklistRow } from './SettingsGroupChecklist';
import { ImportWarningList } from './ImportWarningList';

type Loaded = { bundle: SettingsBundle; warnings: BundleWarning[]; fileName: string };

/**
 * Import-time wording that differs from the export-time wording. Snippets are
 * the one group whose apply is a REPLACE rather than a field-wise merge: a
 * per-engine list is an array, and arrays replace. Engines the file does not
 * mention keep their local snippets untouched. Saying so on the checklist is
 * cheaper and more honest than a bespoke merge-by-id, and it stops the one
 * silent data loss this feature could cause.
 */
const IMPORT_NOTES: Partial<Record<SettingsBundleGroupId, string>> = {
  snippets:
    'Replaces this machine\'s snippets for the engines the backup covers. ' +
    'Snippets for any other engine are left alone.',
};

function rowsFor(bundle: SettingsBundle): ChecklistRow[] {
  const rows: ChecklistRow[] = SETTINGS_BUNDLE_GROUPS.filter((g) => bundle.groups[g.id]).map((g) => ({
    id: g.id,
    label: g.label,
    description: IMPORT_NOTES[g.id] ?? g.description,
    optIn: g.optIn,
  }));
  if (bundle.metaInfo?.length) {
    rows.push({
      id: 'metaInfo',
      label: `Database meta info (${bundle.metaInfo.length} database${bundle.metaInfo.length === 1 ? '' : 's'})`,
      description: METAINFO_CAVEAT,
      // Writes to disk outside the settings stores; never a default.
      optIn: true,
    });
  }
  return rows;
}

export function SettingsImportPanel() {
  const { track } = useAnalytics();
  const [loaded, setLoaded] = useState<Loaded | null>(null);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [busy, setBusy] = useState(false);

  const onChooseFile = async () => {
    setBusy(true);
    try {
      const read = await importBundleFromFile();
      if (!read.success) {
        if (read.cancelled) return;
        toast.error({ title: 'Could not open that file', description: read.error });
        return;
      }
      const parsed = parseSettingsBundle(read.json);
      if (!parsed.ok) {
        // Nothing has been applied at this point, and nothing will be.
        setLoaded(null);
        toast.error({ title: 'That backup could not be imported', description: parsed.error });
        return;
      }
      const fileName = read.filePath.split(/[\\/]/).pop() ?? 'settings.json';
      setLoaded({ bundle: parsed.bundle, warnings: parsed.warnings, fileName });
      // Mirror the export panel: groups the manifest marks opt-in start
      // UNTICKED, and so does meta info. Pre-ticking them would have made the
      // snippet REPLACE and a disk write the default outcome of pressing a
      // button labelled "Choose backup file…".
      setSelected(new Set(rowsFor(parsed.bundle).filter((r) => !r.optIn).map((r) => r.id)));
    } finally {
      setBusy(false);
    }
  };

  const toggle = (id: string, next: boolean) =>
    setSelected((current) => {
      const copy = new Set(current);
      if (next) copy.add(id);
      else copy.delete(id);
      return copy;
    });

  const onApply = async () => {
    if (!loaded) return;
    setBusy(true);
    try {
      const groupIds = [...selected].filter((id) => id !== 'metaInfo') as SettingsBundleGroupId[];
      const result = await applySettingsBundle({
        bundle: loaded.bundle,
        groupIds,
        includeMetaInfo: selected.has('metaInfo'),
        targets: { store: storeTarget, importMetaInfo: importMetaInfoRecord },
      });

      track('feature:settings_import', { groups: [...selected].sort().join(',') });

      const meta = result.metaInfo;
      const parts = [`${result.appliedGroups.length} setting group(s) applied`];
      if (meta) parts.push(`${meta.applied} of ${meta.total} meta-info record(s)`);
      if (meta?.failed) parts.push(`${meta.failed} could not be written`);
      if (result.restartNeeded) parts.push('restart to finish applying');

      toast.success({ title: 'Settings imported', description: parts.join(' · ') });
      setLoaded(null);
      setSelected(new Set());
    } catch (error) {
      toast.error({
        title: 'Import failed — nothing was changed',
        description: error instanceof Error ? error.message : 'Unknown error',
      });
    } finally {
      setBusy(false);
    }
  };

  const rows = loaded ? rowsFor(loaded.bundle) : [];

  return (
    <section className="space-y-3" data-testid="settings-import-panel">
      <div className="text-sm font-medium">Import</div>
      <p className="text-xs text-muted-foreground">
        Pick a backup to see what it contains. Nothing changes until you choose what to apply.
      </p>

      <Button
        variant="outline"
        onClick={onChooseFile}
        disabled={busy}
        data-testid="settings-import-choose-button"
        className="gap-2"
      >
        <FileUp className="h-4 w-4" />
        Choose backup file…
      </Button>

      {loaded && (
        <div className="space-y-3" data-testid="settings-import-preview">
          <div className="text-xs text-muted-foreground">
            Read <span className="font-medium text-foreground">{loaded.fileName}</span>
            {loaded.bundle.exportedAt ? `, exported ${loaded.bundle.exportedAt.slice(0, 10)}` : ''}.
          </div>

          <SettingsGroupChecklist
            rows={rows}
            selected={selected}
            onToggle={toggle}
            idPrefix="settings-import"
          />

          <ImportWarningList warnings={loaded.warnings} />

          <Button
            onClick={onApply}
            disabled={busy || selected.size === 0}
            data-testid="settings-import-apply-button"
            className="gap-2"
          >
            <Upload className="h-4 w-4" />
            {busy ? 'Importing…' : 'Import selected'}
          </Button>
        </div>
      )}
    </section>
  );
}
